// SIM-2914 slice 2: rendering the resolved scope a fetch ran under.
//
// Formatting only. The COMPARISON primitives — `scopeKey`, `sameScope`,
// `diffScope` — live in `@Simplicity-Education/core`: the volume band
// partitions on exactly that key and the verdict is computed server-side, so a
// copy here would be a second comparison rule to drift.

import type { TableRunScope } from "@Simplicity-Education/core";

export interface ScopeLine {
  /** Dimension name, also used as the diff-line subject. */
  label: string;
  text: string;
  /**
   * `warning` is for scope that silently removes records a reader would expect
   * to be there — today only `minStartIndex`, which is being retired (SIM-1408).
   */
  tone: "normal" | "warning";
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/**
 * Predicate scoping: what bounded the rows the source was asked for. Split from the positional
 * lines below because the two families fail differently — a predicate changes the denominator, a
 * pagination window changes which slice was walked — and to keep each under the complexity cap.
 */
function predicateLines(scope: TableRunScope): ScopeLine[] {
  const lines: ScopeLine[] = [];

  if (scope.reference) {
    lines.push({
      label: "reference",
      text: "reference table — pulled district-wide by knowledge-file policy, not by config",
      tone: "normal",
    });
  }

  if (scope.unfiltered) {
    lines.push({
      label: "unfiltered",
      text: "unfiltered — every scoping dimension bypassed by an override",
      tone: "normal",
    });
  }

  if (scope.date) {
    // The token explains intent and the value explains the count; a reader
    // needs both, and they are only equal when the config held a literal date.
    const token = scope.date.token && scope.date.token !== scope.date.value;
    lines.push({
      label: "date",
      text: `${scope.date.column} ≥ ${scope.date.value}${token ? ` (${scope.date.token})` : ""}`,
      tone: "normal",
    });
  }

  if (scope.school) {
    lines.push({
      label: "schools",
      text:
        scope.school.of == null
          ? plural(scope.school.count, "school", "schools")
          : `${scope.school.count.toLocaleString()} of ${scope.school.of.toLocaleString()} schools`,
      tone: "normal",
    });
  }

  if (scope.student) {
    // Intent, NOT a cardinality. `getScopeDescriptor` hardcodes `count: 0` for
    // every 1c table — it is contractually side-effect-free and the real pool
    // size is only known after `getStudentIdentifiers()` has fetched it. Showing
    // the number read as "we asked for zero students and got 47,780 rows".
    lines.push({
      label: "students",
      text: "scoped to the in-scope student list (1c)",
      tone: "normal",
    });
  }

  if (scope.cascade) {
    const size = scope.cascade.count == null ? "" : ` (${scope.cascade.count.toLocaleString()})`;
    lines.push({
      label: "cascade",
      text: `keyed off ${scope.cascade.parent}.${scope.cascade.joinColumn}${size}`,
      tone: "normal",
    });
  }

  if (scope.disabled.length > 0) {
    lines.push({
      label: "disabled",
      text: `disabled by override: ${scope.disabled.join(", ")}`,
      tone: "normal",
    });
  }

  return lines;
}

/** Positional scoping: which slice of the table the walk actually covered. */
function positionalLines(scope: TableRunScope): ScopeLine[] {
  const lines: ScopeLine[] = [];

  if (scope.mode) {
    lines.push({
      label: "mode",
      text:
        scope.mode === "incremental"
          ? "incremental — walked from the stored cursor, not the start"
          : "full",
      tone: "normal",
    });
  }

  if (scope.minStartIndex != null) {
    lines.push({
      label: "floor",
      text: `skips the first ${scope.minStartIndex.toLocaleString()} records (minStartIndex) — being retired, SIM-1408`,
      tone: "warning",
    });
  }

  return lines;
}

/**
 * The lines of the scope block, in the order a reader checks them: what bounded the rows, how the
 * walk was bounded, then the deprecation warning. Only dimensions that applied are returned, and an
 * empty result means the fetch was genuinely unscoped — which the caller states rather than
 * rendering nothing.
 */
export function describeScope(scope: TableRunScope): ScopeLine[] {
  return [...predicateLines(scope), ...positionalLines(scope)];
}
